import type { JsonObject } from "@/api";
import type { LeafValue, RecipeFieldSpec, ScalarValue } from "./recipeTypes";

// 큐레이션 폼 값의 순수 변환 함수들. 편집기 문자열 ↔ 타입 값 변환, dot-path 읽기, leaf 동치 비교를 한다.
// YAML 라이브러리·카탈로그에 의존하지 않는다.

// YAML/Hydra 스칼라로 인정할 숫자 형태. 지수표기(1e-5·1e-08·1e-8)와 소수(.5·5.)를 모두 포함한다.
// 버전 문자열("3.4.19")·16진수·Infinity 는 매칭되지 않아 문자열로 남는다.
const NUMBER_RE = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/;

// 문자열을 YAML 스칼라로 해석한다. 숫자 판정은 JS 문자열 왕복이 아니라 정규식으로 하므로
// 지수표기가 문자열로 잘못 해석되지 않는다.
export function parseScalar(raw: string): ScalarValue {
  const trimmed = raw.trim();
  if (trimmed === "true") {
    return true;
  }
  if (trimmed === "false") {
    return false;
  }
  if (trimmed === "null") {
    return null;
  }
  if (NUMBER_RE.test(trimmed) && Number.isFinite(Number(trimmed))) {
    return Number(trimmed);
  }
  return trimmed;
}

// 편집기 문자열 → 필드 타입에 맞춘 값. 빈 입력은 undefined(= no-op, base 값 유지)로 알린다.
// nullable 필드에 "null" 을 입력하면 명시적 JSON null 을 반환한다(문자열 "null" 과 구분).
export function coerceFieldValue(raw: string, spec: RecipeFieldSpec): LeafValue | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (spec.nullable && trimmed === "null") {
    return null;
  }
  switch (spec.type) {
    case "boolean":
      // 토글이 아닌 자유 입력도 방어적으로 처리한다. true/false 외에는 무효라 no-op 이다.
      if (trimmed === "true") {
        return true;
      }
      if (trimmed === "false") {
        return false;
      }
      return undefined;
    case "integer":
    case "number": {
      const scalar = parseScalar(trimmed);
      return typeof scalar === "number" ? scalar : undefined;
    }
    case "list":
      return parseList(trimmed);
    case "enum":
      // 미확증 enum 은 카탈로그가 string 으로 두므로, 여기 오는 값은 옵션 안의 값으로 본다. BE 가 최종 검증한다.
      return trimmed;
    case "string":
      return trimmed;
  }
}

// override 값 문자열 해석. "[...]" 은 스칼라 리스트로, 그 외는 parseScalar 로 해석한다. mux shape 같은
// 리스트 override 가 문자열로 잘못 전달되지 않도록 payload 빌드가 쓴다.
export function parseListOrScalar(raw: string): ScalarValue | ScalarValue[] {
  const trimmed = raw.trim();
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    return parseList(trimmed);
  }
  return parseScalar(trimmed);
}

// "[0.95, 0.999]" 또는 "0.95, 0.999" → 스칼라 리스트. 빈 원소는 버린다. "[]" → 빈 리스트.
function parseList(trimmed: string): ScalarValue[] {
  const inner = trimmed.startsWith("[") && trimmed.endsWith("]") ? trimmed.slice(1, -1) : trimmed;
  return inner
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map(parseScalar);
}

// 타입 값 → 편집기 문자열. 리스트는 "[a, b]" 로, null 은 "null" 로 표기한다.
export function formatFieldValue(value: LeafValue | undefined): string {
  if (value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return `[${value.map(formatScalar).join(", ")}]`;
  }
  return formatScalar(value);
}

function formatScalar(value: ScalarValue): string {
  if (value === null) {
    return "null";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
}

// dot-notation 경로로 중첩 객체를 읽는다. 중간이 객체가 아니거나 경로가 없으면 undefined.
export function getAtPath(data: JsonObject, path: string): unknown {
  let cursor: unknown = data;
  for (const key of path.split(".")) {
    if (cursor === null || typeof cursor !== "object" || Array.isArray(cursor)) {
      return undefined;
    }
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

// 값이 OmegaConf 인터폴레이션(${...})을 포함하는지. 리스트는 원소별로 검사한다. 인터폴레이션은
// 커스텀 resolver 라 클라이언트에서 해석할 수 없다. 편집 잠금·override 방출 금지·round-trip 보존에 쓴다.
export function isInterpolationString(value: unknown): boolean {
  if (typeof value === "string") {
    return value.includes("${");
  }
  if (Array.isArray(value)) {
    return value.some(isInterpolationString);
  }
  return false;
}

// 임의의 파싱값을 leaf(스칼라·스칼라 리스트)로 좁힌다. 중첩 맵은 leaf 가 아니므로 undefined 다.
// null 은 그대로 유지한다(명시적 null 과 부재를 구분). 인터폴레이션 문자열은 문자열로 남는다.
export function asLeaf(value: unknown): LeafValue | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (Array.isArray(value)) {
    return value as ScalarValue[];
  }
  if (typeof value === "object") {
    return undefined;
  }
  return value as ScalarValue;
}

// leaf 값 동치. 리스트는 원소별로, 숫자는 값으로 비교하므로 표기 차이(1e-8 vs 0.00000001)를 같게 본다.
export function deepEqualLeaf(a: LeafValue | undefined, b: LeafValue | undefined): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((element, index) => element === b[index]);
  }
  return a === b;
}
